import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Typography,
  Space,
  Button,
  Tag,
  Divider,
  Collapse,
  Spin,
  message,
  notification,
  Tooltip,
  Row,
  Col,
  Alert,
  List,
  Checkbox
} from 'antd';
import {
  EditOutlined,
  DownloadOutlined,
  CopyOutlined,
  PrinterOutlined,
  HistoryOutlined,
  UserOutlined,
  CalendarOutlined,
  RobotOutlined,
  FileTextOutlined,
  EyeOutlined,
  PlusCircleOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { getActionPlanById, exportActionPlanToPDF } from '../services/actionPlanService';
import { useAuth } from '../context/AuthContext';
import axios from '../api';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

/**
 * Action Plan Viewer Component
 * Displays action plan in read-only mode with professional formatting
 */
const ActionPlanViewer = ({ visible, actionPlan, onClose, onEdit }) => {
  const { user, isAuthenticated } = useAuth();
  const [planDetails, setPlanDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [proposedOrders, setProposedOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [creatingOrders, setCreatingOrders] = useState(false);

  // Load full plan details when modal opens
  useEffect(() => {
    if (visible && actionPlan?._id) {
      loadPlanDetails();
    }
  }, [visible, actionPlan?._id]);

  const loadPlanDetails = async () => {
    try {
      setLoading(true);
      const response = await getActionPlanById(actionPlan._id, true);
      if (response.success) {
        const planData = response.data.actionPlan;
        setPlanDetails(planData);

        // Extract orders from AI generation data if available
        if (planData.aiGenerationData?.generationParameters?.orders) {
          const orders = planData.aiGenerationData.generationParameters.orders;
          setProposedOrders(orders);
          setSelectedOrders(orders.map((_, index) => index)); // Select all by default
        } else {
          // Try to extract orders from the saved action plan data
          const extractedOrders = extractOrdersFromActionPlan(planData);
          setProposedOrders(extractedOrders);
          setSelectedOrders(extractedOrders.map((_, index) => index));
        }
      }
    } catch (error) {
      console.error('Error loading plan details:', error);
      message.error('Failed to load plan details');
    } finally {
      setLoading(false);
    }
  };

  // Extract orders from action plan content (fallback method)
  const extractOrdersFromActionPlan = (planData) => {
    const orders = [];

    // Try to extract from sections that might contain orders
    if (planData.sections) {
      planData.sections.forEach(section => {
        if (['labs_needed', 'imaging_procedures', 'referrals', 'nursing_orders', 'vaccinations'].includes(section.sectionId)) {
          // Parse the section content to extract order-like items
          const content = section.currentContent || section.originalContent;
          const lines = content.split('\n').filter(line => line.trim());

          lines.forEach(line => {
            if (line.includes('-') || line.includes('•') || line.match(/^\d+\./)) {
              const cleanLine = line.replace(/^[-•\d.\s]+/, '').trim();
              if (cleanLine.length > 10) { // Only meaningful orders
                orders.push({
                  order: cleanLine,
                  frequency: 'As needed',
                  type: section.sectionId.replace('_needed', '').replace('_procedures', '').replace('_orders', '')
                });
              }
            }
          });
        }
      });
    }

    return orders;
  };

  // Determine order type based on content analysis
  const determineOrderTypeFromContent = (orderText) => {
    const text = orderText.toLowerCase();

    // Laboratory tests
    const labKeywords = [
      'xét nghiệm', 'máu', 'nước tiểu', 'phân', 'glucose', 'cholesterol', 'hba1c',
      'lipid', 'creatinine', 'urea', 'ast', 'alt', 'bilirubin', 'hemoglobin',
      'wbc', 'rbc', 'platelet', 'esr', 'crp', 'tsh', 'vitamin', 'hormone',
      'công thức máu', 'chức năng gan', 'chức năng thận', 'điện giải', 'marker',
      'test', 'lab', 'blood', 'urine', 'serum', 'plasma', 'panel', 'cbc',
      'metabolic panel', 'liver panel', 'lipid panel', 'basic metabolic', 'comprehensive metabolic',
      'complete blood count', 'differential', 'egfr', 'gfr', 'calculation', 'ferritin',
      'microalbumin', 'urinalysis', 'thyroid function', 'free t4', 'hscrp'
    ];

    // Imaging studies
    const imagingKeywords = [
      'x-quang', 'siêu âm', 'ct', 'mri', 'chụp', 'scan', 'echo', 'doppler',
      'x-ray', 'ultrasound', 'tomography', 'resonance', 'echocardiogram',
      'mammography', 'endoscopy', 'colonoscopy', 'ekg', 'ecg', 'holter'
    ];

    // Referrals
    const referralKeywords = [
      'tư vấn', 'chuyên khoa', 'hội chẩn', 'khám chuyên khoa', 'gửi khám',
      'referral', 'consult', 'specialist', 'cardiology', 'neurology',
      'ophthalmology', 'dermatology', 'orthopedic', 'psychiatry', 'psychology',
      'nephrology', 'endocrinology', 'gastroenterology', 'pulmonology',
      'sleep medicine', 'nutrition', 'dietitian', 'physical therapy'
    ];

    // Nursing/Patient Education
    const nursingKeywords = [
      'hướng dẫn', 'giáo dục', 'theo dõi', 'đo huyết áp', 'đo đường huyết',
      'ghi nhật ký', 'tự theo dõi', 'monitoring', 'education', 'instruction',
      'guidance', 'self-care', 'home monitoring', 'patient education'
    ];

    // Vaccinations
    const vaccinationKeywords = [
      'tiêm phòng', 'vaccine', 'vaccination', 'immunization', 'cúm', 'covid',
      'phế cầu', 'uốn ván', 'hpv', 'hepatitis', 'influenza', 'pneumococcal',
      'booster', 'tdap', 'tetanus', 'diphtheria', 'pertussis', 'mmr', 'varicella'
    ];

    // Medications
    const medicationKeywords = [
      'thuốc', 'medication', 'prescription', 'drug', 'tablet', 'capsule',
      'injection', 'insulin', 'metformin', 'aspirin', 'statin', 'ace inhibitor'
    ];

    // Check each category
    if (labKeywords.some(keyword => text.includes(keyword))) {
      return 'Laboratory';
    }

    if (imagingKeywords.some(keyword => text.includes(keyword))) {
      return 'Radiology';
    }

    if (referralKeywords.some(keyword => text.includes(keyword))) {
      return 'Referral';
    }

    if (vaccinationKeywords.some(keyword => text.includes(keyword))) {
      return 'Vaccine';
    }

    if (medicationKeywords.some(keyword => text.includes(keyword))) {
      return 'Medication';
    }

    if (nursingKeywords.some(keyword => text.includes(keyword))) {
      return 'Nursing';
    }

    // Default fallback
    return 'Other';
  };

  // Transform order format (reuse from AIActionPlan)
  const transformAIOrderToBackendFormat = (aiOrder, patientId) => {
    console.log('[ActionPlanViewer] Transforming order:', aiOrder);

    // Check authentication
    if (!isAuthenticated || !user) {
      throw new Error('User not authenticated - please log in again');
    }

    if (!user._id && !user.id) {
      throw new Error('User not authenticated - please log in again');
    }

    // Determine order type based on AI order structure
    let orderType = 'Other';
    let orderDetails = {};

    // Handle different AI order formats
    if (aiOrder.type) {
      const typeMapping = {
        'lab': 'Laboratory',
        'labs': 'Laboratory',
        'imaging': 'Radiology',
        'clinic': 'Referral',
        'preventive': 'Preventive Screening',
        'nursing': 'Nursing',
        'referral': 'Referral',
        'referrals': 'Referral',
        'medication': 'Medication',
        'vaccinations': 'Vaccine'
      };

      orderType = typeMapping[aiOrder.type] || 'Other';
      orderDetails = {
        description: aiOrder.description || aiOrder.order,
        instructions: aiOrder.description || aiOrder.order,
        system: aiOrder.system
      };

    } else if (aiOrder.order) {
      // Intelligently determine order type based on content
      orderType = determineOrderTypeFromContent(aiOrder.order);
      console.log(`[ActionPlanViewer] Detected order type: "${orderType}" for order: "${aiOrder.order}"`);
      orderDetails = {
        description: aiOrder.order,
        instructions: aiOrder.indication || aiOrder.order,
        system: aiOrder.system
      };
    }

    // Add specific fields based on detected order type for UI display
    if (orderType === 'Laboratory') {
      orderDetails.testName = orderDetails.description;
      orderDetails.specimenType = 'Blood'; // Default
    } else if (orderType === 'Radiology') {
      orderDetails.studyDescription = orderDetails.description;
      orderDetails.reasonForStudy = orderDetails.system || 'Clinical indication';
    } else if (orderType === 'Referral') {
      orderDetails.referredToProvider = orderDetails.system || 'Specialist';
      orderDetails.reasonForReferral = orderDetails.description;
    }

    // Parse frequency
    let frequency = 'One-time';
    const frequencyText = aiOrder.frequency || aiOrder.order || '';

    if (frequencyText) {
      const freq = frequencyText.toLowerCase();
      if (freq.includes('mỗi ngày') || freq.includes('hàng ngày') || freq.includes('daily')) {
        frequency = 'Daily';
      } else if (freq.includes('mỗi tuần') || freq.includes('hàng tuần') || freq.includes('weekly')) {
        frequency = 'Weekly';
      } else if (freq.includes('mỗi tháng') || freq.includes('hàng tháng') || freq.includes('monthly')) {
        frequency = 'Monthly';
      } else if (freq.includes('mỗi năm') || freq.includes('hàng năm') || freq.includes('annually')) {
        frequency = 'Annually';
      } else if (freq.includes('6 tháng') || freq.includes('6 months')) {
        frequency = 'Every 6 months';
      } else if (freq.includes('3 tháng') || freq.includes('3 months')) {
        frequency = 'Every 3 months';
      } else if (freq.includes('1 tháng') || freq.includes('1 month')) {
        frequency = 'Monthly';
      }
    }

    // Set dates
    const startDate = new Date();
    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 1);

    return {
      orderType,
      frequency,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      status: 'Pending',
      patient: planDetails.patient,
      orderingProvider: user._id || user.id,
      orderDetails
    };
  };

  // Handle order creation
  const handleCreateOrders = async () => {
    console.log('[ActionPlanViewer] handleCreateOrders called');

    if (selectedOrders.length === 0) {
      message.warning('No orders selected to create.');
      return;
    }

    if (!isAuthenticated || !user) {
      message.error('You must be logged in to create orders. Please log in and try again.');
      return;
    }

    console.log('[ActionPlanViewer] Starting order creation process');
    setCreatingOrders(true);
    const ordersToCreate = selectedOrders.map(index => proposedOrders[index]);
    let successCount = 0;
    let errorCount = 0;

    console.log('[ActionPlanViewer] Orders to create:', ordersToCreate);
    const hideLoading = message.loading(`Creating ${ordersToCreate.length} orders...`, 0);

    for (const order of ordersToCreate) {
      try {
        console.log('[ActionPlanViewer] Processing order:', order);
        const orderPayload = transformAIOrderToBackendFormat(order, planDetails.patient);
        console.log('[ActionPlanViewer] Order payload:', orderPayload);

        const response = await axios.post(`/patients/${planDetails.patient}/orders`, orderPayload);
        console.log('[ActionPlanViewer] Order created successfully:', response.data);
        successCount++;
      } catch (error) {
        console.error('[ActionPlanViewer] Error creating order:', error);
        console.error('[ActionPlanViewer] Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        errorCount++;
      }
    }

    console.log('[ActionPlanViewer] Order creation completed. Success:', successCount, 'Errors:', errorCount);

    hideLoading();
    setCreatingOrders(false);

    // Add slight delay to ensure loading message is cleared before showing result
    setTimeout(() => {
      console.log('[ActionPlanViewer] Showing result message');

      // Always show result message
      if (successCount > 0 && errorCount === 0) {
        console.log('[ActionPlanViewer] Showing success message');

        // Use Modal.success for guaranteed visibility
        Modal.success({
          title: '🎉 Orders Created Successfully!',
          content: `Successfully created ${successCount} order(s) from the action plan. The orders have been added to the patient's chart.`,
          okText: 'Great!',
          centered: true,
          width: 500,
          zIndex: 10001,
        });

        // Also use browser alert as absolute fallback
        setTimeout(() => {
          alert(`✅ SUCCESS: Created ${successCount} order(s) successfully!`);
        }, 500);
      } else if (successCount > 0 && errorCount > 0) {
        console.log('[ActionPlanViewer] Showing partial success message');
        message.warning({
          content: `Created ${successCount} order(s) successfully, but failed to create ${errorCount} order(s). Please check the console for details.`,
          duration: 6,
          style: {
            marginTop: '20vh',
          },
        });
      } else if (successCount === 0 && errorCount > 0) {
        console.log('[ActionPlanViewer] Showing error message');
        message.error({
          content: `Failed to create all ${errorCount} order(s). Please check the console for details.`,
          duration: 6,
          style: {
            marginTop: '20vh',
          },
        });
      } else {
        // This shouldn't happen, but just in case
        console.log('[ActionPlanViewer] Showing info message');
        message.info({
          content: 'No orders were processed.',
          duration: 3,
          style: {
            marginTop: '20vh',
          },
        });
      }

      // Clear selected orders after showing the result message
      setSelectedOrders([]);
    }, 300);
  };

  const handleExportPDF = async () => {
    try {
      setExporting(true);
      // For now, just print the page - PDF export can be added later
      window.print();
      message.success('Action plan ready for printing/PDF export');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      message.error('Failed to export action plan');
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Render order details (reuse from AIActionPlan)
  const renderOrderDetails = (order) => {
    // Handle format with type: { type: "...", description: "...", frequency: "...", system: "..." }
    if (order.type && order.description) {
      const getTypeColor = (type) => {
        const colors = {
          'lab': 'red',
          'labs': 'red',
          'imaging': 'blue',
          'referral': 'green',
          'referrals': 'green',
          'nursing': 'purple',
          'vaccinations': 'orange',
          'medication': 'cyan'
        };
        return colors[type] || 'default';
      };

      return (
        <div>
          <Text strong>{order.description}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color={getTypeColor(order.type)}>{order.type.toUpperCase()}</Tag>
            {order.frequency && <Tag color="green">{order.frequency}</Tag>}
            {order.system && <Tag color="blue">{order.system}</Tag>}
          </div>
        </div>
      );
    }

    // Handle format with system: { order: "...", system: "..." } (with or without frequency)
    if (order.order && order.system) {
      return (
        <div>
          <Text strong>{order.order}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue">Order</Tag>
            {order.frequency && <Tag color="green">{order.frequency}</Tag>}
            <Tag color="purple">{order.system}</Tag>
          </div>
        </div>
      );
    }

    // Handle simple format: { order: "...", frequency: "..." } (without system)
    if (order.order && order.frequency && !order.system) {
      return (
        <div>
          <Text strong>{order.order}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue">Order</Tag>
            <Tag color="green">{order.frequency}</Tag>
          </div>
        </div>
      );
    }

    // Handle simple format: { order: "..." } (only order text)
    if (order.order && !order.frequency && !order.system) {
      return (
        <div>
          <Text strong>{order.order}</Text>
          <div style={{ marginTop: 4 }}>
            <Tag color="blue">Order</Tag>
          </div>
        </div>
      );
    }

    // Fallback
    return <Text>{JSON.stringify(order)}</Text>;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'orange',
      active: 'green',
      archived: 'blue',
      cancelled: 'red'
    };
    return colors[status] || 'default';
  };

  const getPlanTypeIcon = (planType) => {
    // Use generic file icon for all plan types
    return <FileTextOutlined />;
  };

  const renderSectionContent = (section) => {
    if (!section.currentContent) return null;

    return (
      <div className="action-plan-section-content">
        <ReactMarkdown>{section.currentContent}</ReactMarkdown>
      </div>
    );
  };

  const renderPlanMetadata = () => {
    if (!planDetails) return null;

    return (
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={3}>
            <Space direction="vertical" size="small">
              <Text type="secondary">Status</Text>
              <Tag color={getStatusColor(planDetails.status)}>
                {planDetails.status?.toUpperCase()}
              </Tag>
            </Space>
          </Col>
          <Col span={3}>
            <Space direction="vertical" size="small">
              <Text type="secondary">Language</Text>
              <Tag color="blue">
                {(() => {
                  const uiLanguage = planDetails.aiGenerationData?.generationParameters?.uiLanguage;
                  console.log(`Action Plan ${planDetails._id} language data:`, {
                    uiLanguage,
                    language: planDetails.language,
                    hasAiData: !!planDetails.aiGenerationData,
                    hasGenParams: !!planDetails.aiGenerationData?.generationParameters
                  });
                  return uiLanguage === 'vietnamese' ? 'VI' : 'EN';
                })()}
              </Tag>
            </Space>
          </Col>
          <Col span={4}>
            <Space direction="vertical" size="small">
              <Text type="secondary">Created By</Text>
              <Space>
                <UserOutlined />
                <Text>
                  {planDetails.createdBy?.name || 'Unknown'}
                </Text>
              </Space>
            </Space>
          </Col>
          <Col span={4}>
            <Space direction="vertical" size="small">
              <Text type="secondary">Created</Text>
              <Space>
                <CalendarOutlined />
                <Text>{new Date(planDetails.createdAt).toLocaleDateString()}</Text>
              </Space>
            </Space>
          </Col>
          <Col span={4}>
            <Space direction="vertical" size="small">
              <Text type="secondary">Modified</Text>
              <Space>
                <CalendarOutlined />
                <Text>{new Date(planDetails.updatedAt).toLocaleDateString()}</Text>
              </Space>
            </Space>
          </Col>
          <Col span={6}>
            <Space direction="vertical" size="small">
              <Text type="secondary">Plan Period</Text>
              <Text>
                {planDetails.planPeriod?.startDate && planDetails.planPeriod?.endDate
                  ? `${new Date(planDetails.planPeriod.startDate).toLocaleDateString()} - ${new Date(planDetails.planPeriod.endDate).toLocaleDateString()}`
                  : '1 Year Plan'
                }
              </Text>
            </Space>
          </Col>

        </Row>
      </Card>
    );
  };

  const renderActionPlanSections = () => {
    if (!planDetails?.sections || planDetails.sections.length === 0) {
      return <Text type="secondary">No sections available</Text>;
    }

    const sectionOrder = [
      'tier_recommendations',
      'active_problems',
      'medications',
      'labs_needed',
      'imaging_procedures',
      'referrals',
      'smart_goals',
      'vaccinations',
      'nursing_orders',
      'appointments_followup'
    ];

    const sortedSections = planDetails.sections.sort((a, b) => {
      const aIndex = sectionOrder.indexOf(a.sectionId);
      const bIndex = sectionOrder.indexOf(b.sectionId);
      return aIndex - bIndex;
    });

    return (
      <Collapse 
        defaultActiveKey={sortedSections.map(s => s.sectionId)}
        ghost
      >
        {sortedSections.map((section) => (
          <Panel
            header={
              <Space>
                <Text strong>{section.title}</Text>
                {section.isModified && (
                  <Tag color="blue" size="small">Modified</Tag>
                )}
              </Space>
            }
            key={section.sectionId}
          >
            {renderSectionContent(section)}
          </Panel>
        ))}
      </Collapse>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <EyeOutlined />
          <span>{planDetails?.title || 'Action Plan'}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={
        <Space>
          <Button onClick={onClose}>
            Close
          </Button>
          <Button
            icon={<PrinterOutlined />}
            onClick={handlePrint}
          >
            Print
          </Button>
          <Button
            icon={<DownloadOutlined />}
            loading={exporting}
            onClick={handleExportPDF}
          >
            Export PDF
          </Button>
          {proposedOrders.length > 0 && (
            <Button
              type="primary"
              icon={<PlusCircleOutlined />}
              loading={creatingOrders}
              onClick={handleCreateOrders}
              disabled={selectedOrders.length === 0}
            >
              Create Selected Orders ({selectedOrders.length})
            </Button>
          )}
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={onEdit}
          >
            Edit Plan
          </Button>
        </Space>
      }
      className="action-plan-viewer-modal"
    >
      <Spin spinning={loading}>
        {planDetails && (
          <div>
            {/* Plan Metadata */}
            {renderPlanMetadata()}



            {/* Action Plan Content */}
            <Card title="Action Plan Content">
              {renderActionPlanSections()}
            </Card>

            {/* Proposed Orders Section */}
            {proposedOrders.length > 0 && (
              <Card
                title={
                  <Space>
                    <PlusCircleOutlined />
                    <span>Proposed Orders ({proposedOrders.length})</span>
                  </Space>
                }
                style={{ marginTop: 16 }}
              >
                <Alert
                  message="Order Creation"
                  description="Select the orders you want to create from this action plan. These orders will be added to the patient's chart."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <List
                  dataSource={proposedOrders}
                  renderItem={(order, index) => (
                    <List.Item>
                      <Checkbox
                        checked={selectedOrders.includes(index)}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedOrders([...selectedOrders, index]);
                          } else {
                            setSelectedOrders(selectedOrders.filter(i => i !== index));
                          }
                        }}
                      >
                        {renderOrderDetails(order)}
                      </Checkbox>
                    </List.Item>
                  )}
                />

                <div style={{ marginTop: 16, textAlign: 'center' }}>
                  <Space>
                    <Button
                      onClick={() => setSelectedOrders(proposedOrders.map((_, index) => index))}
                      disabled={selectedOrders.length === proposedOrders.length}
                    >
                      Select All
                    </Button>
                    <Button
                      onClick={() => setSelectedOrders([])}
                      disabled={selectedOrders.length === 0}
                    >
                      Clear All
                    </Button>
                  </Space>
                </div>
              </Card>
            )}

          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default ActionPlanViewer;
